import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hasPermission } from '@lognai/plugin-sdk';
import { AskLognAIIcon } from '@lognai/plugin-ai-core-ui';
import {
  Badge,
  Button,
  Card,
  Input,
  PageLayout,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@lognai/synapse-design-system-v2';
import {
  BarChart3,
  Eye,
  LayoutDashboard,
  Loader2,
  RefreshCw,
  Save,
  Send,
  Table2,
} from '@lognai/synapse-design-system-v2/icons';
import type {
  AnalysisSpec,
  AnalyticsBootstrap,
  ChartTemplate,
  ChartThemePayload,
  Dataset,
  VisualAnswer,
  VisualDefinition,
} from '@lognai/plugin-analytics';
import { AnalyticsHeader } from './AnalyticsHeader';
import { VisualDetailModal } from './VisualDetailModal';
import { VisualEnlargeButton } from './VisualEnlargeButton';
import { HighchartsRenderer, type DrillSelection } from './HighchartsRenderer';
import { InsightPanel } from './InsightPanel';
import { VisualDataTable } from './VisualDataTable';
import { VisualSources, visualDate } from './VisualSources';
import { SourcePicker } from './creator/SourcePicker';
import { BuilderFields } from './creator/BuilderFields';
import { ChartChoices } from './creator/ChartChoices';
import { FilterBuilder } from './creator/FilterBuilder';
import { BaselineComparisonEditor } from './creator/BaselineComparisonEditor';
import {
  analysisDomains,
  analysisKey,
  compatibleTemplatesFor,
  datasetViewFor,
  initialAnalysis,
  memberLabel,
  recommendTemplate,
  starterQuestions,
  validDateRange,
} from './creator-helpers';
import {
  askAnalytics,
  createVisual,
  executeAnalysis,
  getAnalyticsBootstrap,
  getDashboard,
  getVisual,
  invalidateAnalyticsBootstrap,
  makeDashboardEditable,
  recordVisualUsage,
  resolveChartTheme,
  updateDashboard,
  type AnalysisResult,
} from './api';
import { getDataset, listDatasets } from './dataset-api';
import { DATASET_CHANGED } from './dataset-refresh';
import { businessLabel, userFacingAnalyticsError } from '../lib/user-facing';

type CreatorMode = 'ai' | 'guided';
const PROGRESS = [
  'Understanding your question',
  'Choosing the right measures',
  'Analyzing your data',
  'Designing the visual',
];
const EMPTY_ANALYSIS = initialAnalysis('');

function BuilderSection({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 p-5">
      <div className="flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-md bg-primary-subtle text-xs font-semibold text-primary">
          {step}
        </span>
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function VisualCreatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get('source');
  const requestedDataset = searchParams.get('dataset');
  const sequence = useRef(0);
  const saving = useRef(false);
  const saveSectionRef = useRef<HTMLElement>(null);
  const chartRegionRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(420);
  const [bootstrap, setBootstrap] = useState<AnalyticsBootstrap>();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [mode, setMode] = useState<CreatorMode>('ai');
  const [draft, setDraft] = useState<AnalysisSpec>(EMPTY_ANALYSIS);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [origin, setOrigin] = useState<'ai' | 'manual'>('manual');
  const [busy, setBusy] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sourceWarning, setSourceWarning] = useState<string>();
  const [sourceUnavailable, setSourceUnavailable] = useState(false);
  const [snapshotChanged, setSnapshotChanged] = useState(false);
  const [success, setSuccess] = useState<VisualDefinition>();
  const [prompt, setPrompt] = useState('');
  const [refinement, setRefinement] = useState('');
  const [answer, setAnswer] = useState<VisualAnswer>();
  const [preview, setPreview] = useState<AnalysisResult>();
  const [previewTab, setPreviewTab] = useState<'chart' | 'data' | 'sources'>('chart');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSelection, setDetailSelection] = useState<DrillSelection>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessQuestion, setBusinessQuestion] = useState('');
  const [tags, setTags] = useState('');
  const [scope, setScope] = useState<'private' | 'company'>('private');
  const [dashboardId, setDashboardId] = useState('');
  const [theme, setTheme] = useState<ChartThemePayload>();
  const canWrite = hasPermission('analytics:visuals:write');
  const canPublish = hasPermission('analytics:visuals:publish');
  const catalog = bootstrap?.semanticCatalog[draft.view];
  const dataset = datasets.find((item) => datasetViewFor(item.id) === draft.view);
  const compatibleTemplates = useMemo(
    () =>
      compatibleTemplatesFor(
        bootstrap?.supportedTemplates ?? [],
        draft.measures,
        draft.dimensions,
        draft.timeDimension?.member,
        !!draft.baselineComparison
      ),
    [bootstrap?.supportedTemplates, draft.measures, draft.dimensions, draft.timeDimension?.member, draft.baselineComparison]
  );
  const sourceReady =
    !!draft.view && !!catalog && bootstrap?.enabled === true && !sourceUnavailable;
  const stale =
    !!preview && (snapshotChanged || analysisKey(draft) !== analysisKey(preview.analysis));
  const dateValid = validDateRange(draft.timeDimension?.dateRange);
  const canPreview =
    sourceReady &&
    draft.measures.length > 0 &&
    compatibleTemplates.includes(draft.template) &&
    dateValid;
  const previewCurrent = !!preview && !stale && sourceReady && !previewBusy;
  const canSave =
    previewCurrent && !!title.trim() && !saveBusy && (scope === 'company' ? canPublish : canWrite);
  const sourceName = catalog?.title ?? businessLabel(draft.view);
  const questions = starterQuestions(catalog);
  const referencedDatasets = Object.keys(
    preview?.context?.datasetVersions ?? catalog?.datasetVersions ?? {}
  );
  const companyAllowed =
    canPublish &&
    referencedDatasets.every(
      (id) => datasets.find((item) => item.id === id)?.visibility === 'company'
    );
  const dashboards = (bootstrap?.dashboards ?? []).filter(
    (item) =>
      item.status === 'active' &&
      (item.scope === 'company'
        ? scope === 'company' && hasPermission('analytics:dashboards:publish')
        : hasPermission('analytics:dashboards:write'))
  );
  const eligibleDashboard = dashboards.some((item) => item.id === dashboardId);

  useEffect(() => {
    let live = true;
    const generation = ++sequence.current;
    setBusy(true);
    setError(undefined);
    setSourceWarning(undefined);
    setPreview(undefined);
    setSuccess(undefined);
    setAnswer(undefined);
    setSnapshotChanged(false);
    setSourceUnavailable(false);
    const load = async () => {
      const next = await getAnalyticsBootstrap();
      let available: Dataset[] = [];
      if (next.capabilities?.datasets && hasPermission('analytics:datasets:read')) {
        try {
          available = (await listDatasets()).items;
        } catch {
          if (live)
            setSourceWarning(
              'Dataset details could not be loaded. You can retry or use a business module.'
            );
        }
      }
      if (!live || sequence.current !== generation) return;
      setBootstrap(next);
      setDatasets(available);
      setDashboardId(next.defaultDashboardId ?? '');
      if (sourceId) {
        const source = await getVisual(sourceId);
        if (!next.semanticCatalog[source.analysis.view])
          throw new Error('This chart’s data source is unavailable. Choose another source.');
        const result = await executeAnalysis(source.analysis);
        if (!live || sequence.current !== generation) return;
        setMode(source.origin === 'ai' ? 'ai' : 'guided');
        setOrigin(source.origin === 'ai' ? 'ai' : 'manual');
        setDraft(result.analysis);
        setPreview(result);
        setTitle(`${source.title} copy`);
        setDescription(source.description);
        setBusinessQuestion(source.businessQuestion);
        setTags(source.tags.join(', '));
        setScope(source.scope === 'company' ? 'company' : 'private');
      } else {
        const requestedView = requestedDataset ? datasetViewFor(requestedDataset) : '';
        if (requestedDataset && !next.semanticCatalog[requestedView]) {
          setDraft(EMPTY_ANALYSIS);
          setSourceUnavailable(true);
          setError(
            'This dataset is unavailable or you no longer have access. Choose another source or return to Datasets.'
          );
          return;
        }
        const view =
          requestedView ||
          Object.keys(next.semanticCatalog).find((item) => !item.startsWith('dataset_')) ||
          Object.keys(next.semanticCatalog)[0] ||
          '';
        let selected = available.find((item) => datasetViewFor(item.id) === view);
        if (requestedDataset && !selected) {
          selected = await getDataset(requestedDataset);
          if (!live || sequence.current !== generation) return;
          setDatasets([...available, selected]);
        }
        setDraft(initialAnalysis(view, next.semanticCatalog[view], selected));
        if (requestedDataset) setMode('guided');
      }
      if (!next.enabled && live)
        setError('Analytics is currently unavailable. Please try again when your data is ready.');
    };
    void load()
      .catch((cause) => {
        if (live) {
          setSourceUnavailable(true);
          setDraft(EMPTY_ANALYSIS);
          setError(
            userFacingAnalyticsError(cause, 'Chart creation could not be opened. Please try again.')
          );
        }
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
      ++sequence.current;
    };
  }, [sourceId, requestedDataset, loadAttempt]);

  useEffect(() => {
    let live = true;
    void resolveChartTheme()
      .then((resolved) => {
        if (live) setTheme(resolved.payload);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    const refresh = () => {
      ++sequence.current;
      setPreviewBusy(false);
      setSnapshotChanged(true);
      setSuccess(undefined);
      invalidateAnalyticsBootstrap();
      void getAnalyticsBootstrap()
        .then((next) => {
          setBootstrap(next);
          if (draftRef.current.view && (!next.semanticCatalog[draftRef.current.view] ||
              (draftRef.current.baselineComparison && !next.semanticCatalog[draftRef.current.baselineComparison.view]))) {
            setSourceUnavailable(true);
            setError(
              'This data source is no longer available. Choose another source or return to Datasets.'
            );
          }
        })
        .catch(() =>
          setSourceWarning(
            'Source availability could not be refreshed. Retry before building a chart.'
          )
        );
      if (hasPermission('analytics:datasets:read'))
        void listDatasets()
          .then((result) => setDatasets(result.items))
          .catch(() => undefined);
    };
    window.addEventListener(DATASET_CHANGED, refresh);
    return () => window.removeEventListener(DATASET_CHANGED, refresh);
  }, []);
  useEffect(() => setDetailSelection(undefined), [preview]);
  useEffect(() => {
    if (!dashboards.some((item) => item.id === dashboardId))
      setDashboardId(
        dashboards.find((item) => item.id === bootstrap?.defaultDashboardId)?.id ??
          dashboards[0]?.id ??
          ''
      );
  }, [scope, dashboardId, bootstrap?.dashboards, bootstrap?.defaultDashboardId]);
  useLayoutEffect(() => {
    const region = chartRegionRef.current;
    if (!region) return;
    const measure = () =>
      setChartHeight(Math.max(360, Math.floor(region.getBoundingClientRect().height)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    return () => observer.disconnect();
  }, [preview, previewBusy, previewTab]);
  useEffect(() => {
    if (!previewBusy) {
      setProgress(0);
      return;
    }
    const timer = window.setInterval(
      () => setProgress((value) => Math.min(value + 1, PROGRESS.length - 1)),
      1100
    );
    return () => window.clearInterval(timer);
  }, [previewBusy]);

  const updateDraft = (patch: Partial<AnalysisSpec>) => {
    ++sequence.current;
    setPreviewBusy(false);
    setSuccess(undefined);
    setOrigin('manual');
    setError(undefined);
    setDraft((current) => {
      const next = { ...current, ...patch };
      const templates = compatibleTemplatesFor(
        bootstrap?.supportedTemplates ?? [],
        next.measures,
        next.dimensions,
        next.timeDimension?.member,
        !!next.baselineComparison
      );
      if (templates.length && !templates.includes(next.template))
        next.template = recommendTemplate(templates, next).template!;
      return next;
    });
  };
  const changeSource = (view: string) => {
    ++sequence.current;
    setPreviewBusy(false);
    setSourceUnavailable(false);
    setError(undefined);
    setPreview(undefined);
    setAnswer(undefined);
    setSuccess(undefined);
    setSnapshotChanged(false);
    setDetailOpen(false);
    setRefinement('');
    setPrompt('');
    setTitle('');
    setDescription('');
    setBusinessQuestion('');
    setTags('');
    setScope('private');
    setOrigin('manual');
    setDraft(
      initialAnalysis(
        view,
        bootstrap?.semanticCatalog[view],
        datasets.find((item) => datasetViewFor(item.id) === view)
      )
    );
  };
  const acceptPreview = (result: AnalysisResult) => {
    setDraft(result.analysis);
    setPreview(result);
    setSnapshotChanged(false);
    setPreviewTab('chart');
    if (!title) setTitle(result.analysis.title);
  };
  const previewGuided = async (input = draft) => {
    if (!sourceReady || previewBusy || saveBusy) return;
    const generation = ++sequence.current;
    setPreviewBusy(true);
    setError(undefined);
    setSuccess(undefined);
    setAnswer(undefined);
    try {
      const result = await executeAnalysis({ ...input, title: title.trim() || input.title });
      if (generation !== sequence.current) return;
      acceptPreview(result);
    } catch (cause) {
      if (generation === sequence.current) setError(userFacingAnalyticsError(cause));
    } finally {
      if (generation === sequence.current) setPreviewBusy(false);
    }
  };
  const runAsk = async (messageInput?: string, refining = false) => {
    const message = (messageInput ?? (refining ? refinement : prompt)).trim();
    if (!message || !sourceReady || previewBusy || saveBusy || (refining && !previewCurrent))
      return;
    const generation = ++sequence.current;
    setPreviewBusy(true);
    setError(undefined);
    setSuccess(undefined);
    try {
      const result = await askAnalytics({
        message,
        sourceView: draft.view,
        previewOnly: true,
        ...(refining ? { activeAnalysis: draft, refineAnalysis: true } : {}),
      });
      if (generation !== sequence.current) return;
      setOrigin('ai');
      setAnswer(result);
      acceptPreview(result);
      setTitle(result.analysis.title);
      setBusinessQuestion((refining ? businessQuestion || prompt : message).slice(0, 300));
      setDescription(result.insight.summary.slice(0, 600));
      setRefinement('');
      void askAnalytics({
        message,
        sourceView: result.analysis.view,
        activeAnalysis: result.analysis,
        executionId: result.context?.executionId,
      })
        .then((enhanced) => {
          if (generation === sequence.current) {
            setAnswer(enhanced);
            setDescription(enhanced.insight.summary.slice(0, 600));
          }
        })
        .catch(() => undefined);
    } catch (cause) {
      if (generation === sequence.current)
        setError(
          userFacingAnalyticsError(
            cause,
            'LognAI could not answer from the selected source. Try naming a metric and breakdown shown in the builder.'
          )
        );
    } finally {
      if (generation === sequence.current) setPreviewBusy(false);
    }
  };
  const changeTemplate = async (template: ChartTemplate) => {
    if (!previewCurrent || template === draft.template) return;
    updateDraft({ template });
    await previewGuided({ ...draft, template });
  };
  const saveChart = async (): Promise<VisualDefinition> => {
    if (success) return success;
    if (!previewCurrent || !preview || !title.trim())
      throw new Error('Rebuild the preview before saving.');
    for (const [id, version] of Object.entries(preview.context?.datasetVersions ?? {})) {
      if ((await getDataset(id)).activeVersion !== version) {
        setSnapshotChanged(true);
        throw new Error('The dataset changed. Rebuild the preview before saving.');
      }
    }
    if ((draft.view.startsWith('dataset_') || draft.baselineComparison) && !preview.context?.executionId)
      throw new Error('Dataset source details are unavailable. Rebuild the preview before saving.');
    const saved = await createVisual({
      title: title.trim(),
      description: description.trim(),
      businessQuestion: businessQuestion.trim(),
      scope,
      origin,
      analysis: { ...preview.analysis, title: title.trim() },
      presentation: { template: preview.visual.template },
      executionId: preview.context?.executionId,
      domains: analysisDomains(preview.analysis.view),
      workflows: [preview.analysis.view],
      tags: tags
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 20),
    });
    setSuccess(saved);
    void recordVisualUsage(saved.id, 'created').catch(() => undefined);
    return saved;
  };
  const addToDashboard = async (saved: VisualDefinition) => {
    if (!eligibleDashboard) throw new Error('Choose an available dashboard.');
    let dashboard = await getDashboard(dashboardId);
    if (dashboard.scope === 'system') dashboard = await makeDashboardEditable(dashboard.id);
    if (dashboard.scope === 'company' && saved.scope !== 'company')
      throw new Error('Save a company chart before adding it to a company dashboard.');
    const widgetId = crypto.randomUUID();
    const append = (layout: typeof dashboard.layouts.lg, width: number) => [
      ...layout,
      {
        i: widgetId,
        x: 0,
        y: Math.max(0, ...layout.map((item) => item.y + item.h)),
        w: width,
        h: 8,
        minW: Math.min(width, 3),
        minH: 6,
      },
    ];
    await updateDashboard(dashboard.id, dashboard.version, {
      widgets: [...dashboard.widgets, { id: widgetId, visualId: saved.id, filters: [] }],
      layouts: {
        lg: append(dashboard.layouts.lg, 4),
        md: append(dashboard.layouts.md, 5),
        sm: append(dashboard.layouts.sm, 4),
      },
    });
    void recordVisualUsage(saved.id, 'added_to_dashboard').catch(() => undefined);
    navigate(`/analytics/dashboards/${encodeURIComponent(dashboard.id)}`);
  };
  const save = async (withDashboard = false) => {
    if (saving.current || !canSave || (withDashboard && !eligibleDashboard)) return;
    saving.current = true;
    ++sequence.current;
    setSaveBusy(true);
    setError(undefined);
    try {
      const saved = await saveChart();
      if (withDashboard) await addToDashboard(saved);
    } catch (cause) {
      setError(
        userFacingAnalyticsError(
          cause,
          'The chart could not be saved or added to the dashboard. Please try again.'
        )
      );
    } finally {
      saving.current = false;
      setSaveBusy(false);
    }
  };
  const editMetadata = (apply: () => void) => {
    ++sequence.current;
    setPreviewBusy(false);
    setSuccess(undefined);
    apply();
  };
  const sortMembers = [
    ...new Set([
      ...draft.measures,
      ...draft.dimensions,
      ...(draft.timeDimension ? [draft.timeDimension.member] : []),
    ]),
  ];
  const alternatives = preview
    ? compatibleTemplatesFor(
        bootstrap?.supportedTemplates ?? [],
        preview.analysis.measures,
        preview.analysis.dimensions,
        preview.analysis.timeDimension?.member,
        !!preview.analysis.baselineComparison
      ).slice(0, 6)
    : [];
  const setTime = (member: string) => {
    const nextTime =
      member === 'none'
        ? undefined
        : {
            member,
            granularity: draft.timeDimension?.granularity ?? ('month' as const),
            ...(draft.timeDimension?.dateRange ? { dateRange: draft.timeDimension.dateRange } : {}),
          };
    updateDraft({
      timeDimension: nextTime,
      order: nextTime
        ? [{ member, direction: 'asc' }]
        : draft.measures[0]
          ? [{ member: draft.measures[0], direction: 'desc' }]
          : [],
    });
  };

  if (busy)
    return (
      <PageLayout>
        <div className="flex min-h-[420px] items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="animate-spin" /> Loading chart options
        </div>
      </PageLayout>
    );
  return (
    <div className="min-w-0 w-full">
      <AnalyticsHeader
        title="Create Chart"
        subtitle="Choose your data, explore a question, and save a chart you can reuse."
      />
      <PageLayout maxWidth="full" className="min-w-0 w-full !pt-4">
        {error && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive-border bg-destructive-subtle px-4 py-3 text-sm text-destructive-foreground"
          >
            <p>{error}</p>
            {sourceUnavailable && (
              <div className="flex gap-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    invalidateAnalyticsBootstrap();
                    setLoadAttempt((value) => value + 1);
                  }}
                >
                  Try again
                </Button>
                <Link className="self-center underline" to="/analytics/datasets">
                  Return to Datasets
                </Link>
              </div>
            )}
          </div>
        )}
        <SourcePicker
          catalog={bootstrap?.semanticCatalog ?? {}}
          datasets={datasets}
          view={draft.view}
          onChange={changeSource}
          disabled={saveBusy}
          warning={sourceWarning}
        />
        {success && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-success bg-success-subtle px-4 py-3 text-sm text-success-foreground"
          >
            <span>
              <strong>{success.title}</strong> is saved and ready to use.
            </span>
            <Button size="sm" variant="ghost" onClick={() => navigate('/analytics/library')}>
              Open library
            </Button>
          </div>
        )}
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(340px,0.4fr)_minmax(0,0.6fr)]">
          <Card className="min-w-0 overflow-hidden">
            <div className="border-b border-border p-3">
              <Tabs value={mode} onValueChange={(value) => setMode(value as CreatorMode)}>
                <TabsList className="w-full">
                  <TabsTrigger value="ai">
                    <AskLognAIIcon size={15} /> Ask LognAI
                  </TabsTrigger>
                  <TabsTrigger value="guided">
                    <BarChart3 size={15} /> Guided builder
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {mode === 'ai' ? (
              <div className="space-y-4 p-5">
                <div>
                  <h2 className="font-semibold">What do you want to understand?</h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {sourceReady
                      ? `Ask about ${sourceName}. LognAI uses its available fields and approved relationships.`
                      : 'Choose an available data source to start.'}
                  </p>
                </div>
                <Textarea
                  aria-label="Chart question"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  maxLength={1000}
                  rows={5}
                  placeholder={questions[0] ?? 'Ask a question about your data'}
                  disabled={!sourceReady || saveBusy}
                />
                <div className="space-y-2">
                  {questions.map((question) => (
                    <button
                      type="button"
                      key={question}
                      disabled={!sourceReady || saveBusy}
                      className="block w-full rounded-lg border border-border px-3 py-2 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => setPrompt(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
                <Button
                  className="w-full"
                  leftIcon={<AskLognAIIcon size={16} />}
                  isLoading={previewBusy}
                  disabled={!sourceReady || !prompt.trim() || previewBusy || saveBusy}
                  onClick={() => void runAsk()}
                >
                  Create chart
                </Button>
                {preview && (
                  <section className="space-y-3 border-t border-border pt-4">
                    <label className="text-sm font-medium" htmlFor="refine-chart">
                      Refine this chart
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Refinements use the current chart configuration and selected source.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        id="refine-chart"
                        value={refinement}
                        maxLength={1000}
                        disabled={!previewCurrent || saveBusy}
                        onChange={(event) => setRefinement(event.target.value)}
                        placeholder="Compare by another field"
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void runAsk(undefined, true);
                        }}
                      />
                      <Button
                        aria-label="Send refinement"
                        disabled={!previewCurrent || !refinement.trim() || saveBusy}
                        onClick={() => void runAsk(undefined, true)}
                      >
                        <Send size={15} />
                      </Button>
                    </div>
                    {stale && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canPreview || previewBusy || saveBusy}
                        onClick={() => void previewGuided()}
                      >
                        Rebuild preview
                      </Button>
                    )}
                  </section>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                <BuilderSection step={1} title="Metrics and breakdown">
                  <BuilderFields
                    catalog={catalog}
                    dataset={dataset}
                    analysis={draft}
                    onChange={updateDraft}
                    disabled={!sourceReady || saveBusy}
                  />
                </BuilderSection>
                <BuilderSection step={2} title="Dates and ranking">
                  <BaselineComparisonEditor analysis={draft} catalog={bootstrap?.semanticCatalog ?? {}}
                    onChange={updateDraft} disabled={!sourceReady || saveBusy} />
                  {!!catalog?.timeDimensions.length && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                          value={draft.timeDimension?.member ?? 'none'}
                          onValueChange={setTime}
                          disabled={!sourceReady || saveBusy}
                        >
                          <SelectTrigger aria-label="Date grouping">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No date grouping</SelectItem>
                            {catalog.timeDimensions.map((member) => (
                              <SelectItem key={member} value={member}>
                                {memberLabel(member, catalog)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={draft.timeDimension?.granularity ?? 'month'}
                          onValueChange={(granularity) => {
                            if (draft.timeDimension)
                              updateDraft({
                                timeDimension: {
                                  ...draft.timeDimension,
                                  granularity: granularity as 'day' | 'week' | 'month',
                                },
                              });
                          }}
                          disabled={!draft.timeDimension || saveBusy}
                        >
                          <SelectTrigger aria-label="Date interval">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="day">Day</SelectItem>
                            <SelectItem value="week">Week</SelectItem>
                            <SelectItem value="month">Month</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {draft.timeDimension && (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="space-y-1 text-xs">
                              From
                              <Input
                                aria-label="Chart start date"
                                type="date"
                                value={draft.timeDimension.dateRange?.[0] ?? ''}
                                disabled={saveBusy}
                                onChange={(event) =>
                                  updateDraft({
                                    timeDimension: {
                                      ...draft.timeDimension!,
                                      dateRange: [
                                        event.target.value,
                                        draft.timeDimension!.dateRange?.[1] ?? '',
                                      ],
                                    },
                                  })
                                }
                              />
                            </label>
                            <label className="space-y-1 text-xs">
                              To
                              <Input
                                aria-label="Chart end date"
                                type="date"
                                value={draft.timeDimension.dateRange?.[1] ?? ''}
                                disabled={saveBusy}
                                onChange={(event) =>
                                  updateDraft({
                                    timeDimension: {
                                      ...draft.timeDimension!,
                                      dateRange: [
                                        draft.timeDimension!.dateRange?.[0] ?? '',
                                        event.target.value,
                                      ],
                                    },
                                  })
                                }
                              />
                            </label>
                          </div>
                          {draft.timeDimension.dateRange && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={saveBusy}
                              onClick={() =>
                                updateDraft({
                                  timeDimension: {
                                    member: draft.timeDimension!.member,
                                    granularity: draft.timeDimension!.granularity,
                                  },
                                })
                              }
                            >
                              Use all dates
                            </Button>
                          )}
                          {!dateValid && (
                            <p role="status" className="text-xs text-destructive">
                              Choose both dates, with the end on or after the start.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={draft.order?.[0]?.member ?? 'none'}
                      disabled={!sourceReady || saveBusy}
                      onValueChange={(member) =>
                        updateDraft({
                          order:
                            member === 'none'
                              ? []
                              : [{ member, direction: draft.order?.[0]?.direction ?? 'desc' }],
                        })
                      }
                    >
                      <SelectTrigger aria-label="Sort by">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Default order</SelectItem>
                        {sortMembers.map((member) => (
                          <SelectItem key={member} value={member}>
                            {memberLabel(member, catalog)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={draft.order?.[0]?.direction ?? 'desc'}
                      disabled={!draft.order?.length || saveBusy}
                      onValueChange={(direction) =>
                        updateDraft({
                          order: [
                            {
                              member: draft.order![0].member,
                              direction: direction as 'asc' | 'desc',
                            },
                          ],
                        })
                      }
                    >
                      <SelectTrigger aria-label="Sort direction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Descending</SelectItem>
                        <SelectItem value="asc">Ascending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Select
                    value={String(draft.limit ?? 50)}
                    disabled={!sourceReady || saveBusy}
                    onValueChange={(value) => updateDraft({ limit: Number(value) })}
                  >
                    <SelectTrigger aria-label="Result limit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set([5, 10, 20, 50, 100, draft.limit ?? 50])]
                        .sort((a, b) => a - b)
                        .map((limit) => (
                          <SelectItem key={limit} value={String(limit)}>
                            {limit === 100 ? 'All available (up to 100 groups)' : `${limit} groups`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Your sort order determines which groups appear. Charts display up to 100 groups.
                  </p>
                </BuilderSection>
                <BuilderSection step={3} title="Visualization">
                  <ChartChoices
                    templates={compatibleTemplates}
                    analysis={draft}
                    onChange={(template) => updateDraft({ template })}
                    disabled={!sourceReady || saveBusy}
                  />
                </BuilderSection>
                <BuilderSection step={4} title="Filters">
                  <FilterBuilder
                    catalog={catalog}
                    analysis={draft}
                    onChange={(filters) => updateDraft({ filters })}
                    disabled={!sourceReady || saveBusy}
                  />
                  <Button
                    className="w-full"
                    leftIcon={<Eye size={15} />}
                    isLoading={previewBusy}
                    disabled={!canPreview || previewBusy || saveBusy}
                    onClick={() => void previewGuided()}
                  >
                    {stale ? 'Rebuild preview' : 'Build preview'}
                  </Button>
                </BuilderSection>
              </div>
            )}
          </Card>
          <div className="min-w-0 space-y-4 xl:sticky xl:top-3">
            <Card className="group flex min-h-[560px] min-w-0 flex-col overflow-hidden">
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                  <h2 className="text-sm font-semibold">Chart preview</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {preview?.context?.sources.length
                      ? preview.context.sources
                          .map((source) => `${source.name} · v${source.version}`)
                          .join(' · ')
                      : sourceReady
                        ? sourceName
                        : 'Choose a data source'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {preview && (
                    <Badge variant="outline">
                      {stale ? 'Outdated preview' : businessLabel(preview.visual.template)}
                    </Badge>
                  )}
                  {preview && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Save size={14} />}
                        disabled={!previewCurrent || saveBusy}
                        onClick={() =>
                          saveSectionRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                          })
                        }
                      >
                        Save chart…
                      </Button>
                      <VisualEnlargeButton
                        title={preview.analysis.title}
                        disabled={!previewCurrent}
                        onEnlarge={() => setDetailOpen(true)}
                      />
                    </>
                  )}
                </div>
              </header>
              {stale && (
                <div
                  role="status"
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-warning-border bg-warning-subtle px-4 py-3 text-xs text-warning-foreground"
                >
                  <span>
                    {snapshotChanged
                      ? 'Source data changed. Rebuild to review the latest version.'
                      : 'Chart settings changed. Rebuild the preview before saving.'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<RefreshCw size={13} />}
                    disabled={!canPreview || previewBusy || saveBusy}
                    onClick={() => void previewGuided()}
                  >
                    Rebuild preview
                  </Button>
                </div>
              )}
              {preview && (
                <div className="px-4 pt-3">
                  <Tabs
                    value={previewTab}
                    onValueChange={(value) => setPreviewTab(value as typeof previewTab)}
                  >
                    <TabsList>
                      <TabsTrigger value="chart">Chart</TabsTrigger>
                      <TabsTrigger value="data">Data</TabsTrigger>
                      <TabsTrigger value="sources">Sources & quality</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              )}
              <div className="flex min-h-0 flex-1 flex-col p-4">
                {previewBusy ? (
                  <div className="grid min-h-[420px] place-items-center">
                    <div className="text-center">
                      <Loader2 className="mx-auto animate-spin text-primary" />
                      <p role="status" className="mt-3 text-sm font-medium">
                        {PROGRESS[progress]}
                      </p>
                    </div>
                  </div>
                ) : preview ? (
                  <>
                    {previewTab === 'chart' && (
                      <div ref={chartRegionRef} className="min-h-[360px] min-w-0 flex-1">
                        <HighchartsRenderer
                          visual={preview.visual}
                          onSelect={previewCurrent ? setDetailSelection : undefined}
                          height={chartHeight}
                          theme={theme}
                        />
                      </div>
                    )}
                    {previewTab === 'data' && (
                      <div className="min-h-[360px]">
                        <VisualDataTable
                          analysis={preview.analysis}
                          visual={preview.visual}
                          rows={preview.rows}
                          fields={
                            preview.context?.sources[0]?.fields ?? preview.context?.recordColumns
                          }
                          labels={Object.fromEntries([
                            ...Object.entries(
                              bootstrap?.semanticCatalog[preview.analysis.view]?.fieldLabels ?? {}
                            ),
                            ...Object.entries(
                              bootstrap?.semanticCatalog[preview.analysis.view]?.measures ?? {}
                            ).map(([member, info]) => [member, info.label]),
                          ])}
                        />
                        {!preview.rows?.length &&
                          !preview.visual.series.some((series) => series.points.length) && (
                            <p className="py-6 text-sm text-muted-foreground">
                              No data matches these settings.
                            </p>
                          )}
                      </div>
                    )}
                    {previewTab === 'sources' && (
                      <div className="min-h-[360px] space-y-4">
                        <div className="space-y-2 rounded-lg bg-muted/40 p-3 text-xs">
                          <p>
                            <strong>Metrics:</strong>{' '}
                            {preview.analysis.measures
                              .map((member) =>
                                memberLabel(
                                  member,
                                  bootstrap?.semanticCatalog[preview.analysis.view]
                                )
                              )
                              .join(', ')}
                          </p>
                          <p>
                            <strong>Breakdown:</strong>{' '}
                            {preview.analysis.dimensions
                              .map((member) =>
                                memberLabel(
                                  member,
                                  bootstrap?.semanticCatalog[preview.analysis.view]
                                )
                              )
                              .join(', ') || 'None'}
                            {preview.analysis.timeDimension
                              ? ` · ${businessLabel(preview.analysis.timeDimension.granularity)} by ${memberLabel(preview.analysis.timeDimension.member, catalog)}`
                              : ''}
                          </p>
                          {preview.analysis.timeDimension?.dateRange && (
                            <p>
                              <strong>Date range:</strong>{' '}
                              {preview.analysis.timeDimension.dateRange.join(' to ')}
                            </p>
                          )}
                          <p>
                            <strong>Filters:</strong>{' '}
                            {preview.analysis.filters.length
                              ? preview.analysis.filters
                                  .map(
                                    (filter) =>
                                      `${memberLabel(filter.member, catalog)} ${businessLabel(filter.operator)} ${filter.values?.join(', ') ?? ''}`
                                  )
                                  .join('; ')
                              : 'None'}
                          </p>
                          <p>
                            <strong>Order:</strong>{' '}
                            {preview.analysis.order
                              ?.map(
                                (entry) =>
                                  `${memberLabel(entry.member, catalog)} ${entry.direction === 'asc' ? 'ascending' : 'descending'}`
                              )
                              .join(', ') || 'Default'}
                            . Up to {preview.analysis.limit ?? 50} groups.
                          </p>
                        </div>
                        <VisualSources result={preview} expanded />
                      </div>
                    )}
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      {preview.context?.sources.length
                        ? `Published snapshots · Previewed ${visualDate(preview.context.createdAt)}`
                        : 'Current governed business data'}{' '}
                      · Up to {preview.analysis.limit ?? 50} groups under the displayed filters.
                    </p>
                  </>
                ) : (
                  <div className="grid min-h-[420px] place-items-center rounded-lg border border-dashed border-border text-center">
                    <div className="max-w-sm px-4">
                      <Table2 size={25} className="mx-auto text-muted-foreground" />
                      <p className="mt-3 text-sm font-medium">Explore your selected data</p>
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">
                        Ask a question or choose metrics and breakdowns. Your chart, data, and
                        source details will appear here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {preview && (
                <div className="border-t border-border px-4 py-3">
                  <p className="text-xs font-medium text-muted-foreground">Alternative views</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {alternatives.map((template) => (
                      <Button
                        key={template}
                        size="sm"
                        variant={preview.visual.template === template ? 'secondary' : 'outline'}
                        disabled={!previewCurrent || saveBusy}
                        onClick={() => void changeTemplate(template)}
                      >
                        {businessLabel(template)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {answer && !stale && (
                <InsightPanel
                  insight={answer.insight}
                  evidence={answer.evidence}
                  trace={answer.trace}
                  onSuggestedQuestion={(question) => {
                    setRefinement(question);
                    void runAsk(question, true);
                  }}
                  disabled={!previewCurrent || saveBusy}
                />
              )}
            </Card>
            {preview && (
              <section ref={saveSectionRef} className="scroll-mt-4">
                <Card className="space-y-4 p-5">
                  <div className="flex items-center gap-2">
                    <Save size={16} />
                    <h2 className="text-sm font-semibold">Save and reuse</h2>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="space-y-1 text-xs font-medium" htmlFor="visual-title">
                      Title
                      <Input
                        id="visual-title"
                        value={title}
                        maxLength={160}
                        disabled={saveBusy}
                        onChange={(event) => editMetadata(() => setTitle(event.target.value))}
                      />
                    </label>
                    <label className="space-y-1 text-xs font-medium" htmlFor="visual-question">
                      Business question
                      <Input
                        id="visual-question"
                        value={businessQuestion}
                        maxLength={300}
                        disabled={saveBusy}
                        placeholder="What should this chart help answer?"
                        onChange={(event) =>
                          editMetadata(() => setBusinessQuestion(event.target.value))
                        }
                      />
                    </label>
                  </div>
                  <details>
                    <summary className="cursor-pointer text-xs text-primary">
                      Description and tags
                    </summary>
                    <div className="mt-3 space-y-3">
                      <label className="block space-y-1 text-xs" htmlFor="visual-description">
                        Description
                        <Textarea
                          id="visual-description"
                          value={description}
                          rows={2}
                          maxLength={600}
                          disabled={saveBusy}
                          onChange={(event) =>
                            editMetadata(() => setDescription(event.target.value))
                          }
                        />
                      </label>
                      <label className="block space-y-1 text-xs" htmlFor="visual-tags">
                        Tags
                        <Input
                          id="visual-tags"
                          value={tags}
                          disabled={saveBusy}
                          placeholder="revenue, monthly"
                          onChange={(event) => editMetadata(() => setTags(event.target.value))}
                        />
                      </label>
                    </div>
                  </details>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mr-2 text-xs font-medium">Visibility</span>
                    <Button
                      size="sm"
                      variant={scope === 'private' ? 'secondary' : 'outline'}
                      disabled={saveBusy}
                      onClick={() => editMetadata(() => setScope('private'))}
                    >
                      Only me
                    </Button>
                    {canPublish && (
                      <Button
                        size="sm"
                        variant={scope === 'company' ? 'secondary' : 'outline'}
                        disabled={saveBusy || !companyAllowed}
                        onClick={() => editMetadata(() => setScope('company'))}
                      >
                        Company
                      </Button>
                    )}
                  </div>
                  {canPublish && !companyAllowed && (
                    <p className="text-xs text-muted-foreground">
                      Company charts require company access to every contributing dataset.
                    </p>
                  )}
                  {dashboards.length > 0 && (
                    <Select
                      value={dashboardId || undefined}
                      onValueChange={setDashboardId}
                      disabled={saveBusy}
                    >
                      <SelectTrigger aria-label="Destination dashboard">
                        <SelectValue placeholder="Choose a dashboard" />
                      </SelectTrigger>
                      <SelectContent>
                        {dashboards.map((dashboard) => (
                          <SelectItem key={dashboard.id} value={dashboard.id}>
                            {dashboard.name}
                            {dashboard.scope === 'system' ? ' (creates an editable copy)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {!dashboards.length && (
                    <p className="text-xs text-muted-foreground">
                      No editable dashboards are available for this chart’s visibility.
                    </p>
                  )}
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Save size={14} />}
                      isLoading={saveBusy}
                      disabled={!canSave || !!success}
                      onClick={() => void save()}
                    >
                      Save chart
                    </Button>
                    <Button
                      size="sm"
                      leftIcon={<LayoutDashboard size={14} />}
                      disabled={!canSave || !eligibleDashboard}
                      isLoading={saveBusy}
                      onClick={() => void save(true)}
                    >
                      {success ? 'Add to dashboard' : 'Save & add to dashboard'}
                    </Button>
                  </div>
                  {stale && (
                    <p className="text-xs text-muted-foreground">
                      Review a fresh preview to enable saving.
                    </p>
                  )}
                </Card>
              </section>
            )}
          </div>
        </div>
        <VisualDetailModal
          open={detailOpen}
          onOpenChange={setDetailOpen}
          result={preview}
          selection={detailSelection}
          theme={theme}
        />
      </PageLayout>
    </div>
  );
}

export default VisualCreatorPage;
